import React, {useState} from 'react';
import { Box, Typography, Grid, Card, CardContent, LinearProgress } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import KazakhstanMap from "../../../Map";
import {kazakhstanRegions} from "../../../../../model/regions";
import { useTranslation } from 'react-i18next';



const KazakInvestAnalytics = () => {
    const { t } = useTranslation();

    const getCountryData = (t: any) => [
        { name: t('kazakInvest.countries.china'), value: 24.4 },
        { name: t('kazakInvest.countries.usa'), value: 21.7 },
        { name: t('kazakInvest.countries.states'), value: 17.9 },
        { name: t('kazakInvest.countries.netherlands'), value: 16.2 },
        { name: t('kazakInvest.countries.turkey'), value: 14.7 },
        { name: t('kazakInvest.countries.southKorea'), value: 13 },
        { name: t('kazakInvest.countries.uzbekistan'), value: 10.6 },
        { name: t('kazakInvest.countries.greatBritain'), value: 9.9 },
        { name: t('kazakInvest.countries.switzerland'), value: 9.1 },
        { name: t('kazakInvest.countries.ukraine'), value: 8.5 }
    ];

    const getSuppliersData = (t: any) => [
        { name: 'ZhanaTLM LLP', value: 32.9 },
        { name: 'Alyntys.kz', value: 31.7 },
        { name: 'BI Group', value: 27.9 },
        { name: t('kazakInvest.suppliers.altynZol'), value: 22.7 }
    ];

    const getTenderStats = (t: any) => [
        { month: t('kazakInvest.months.jan'), tenders: 10, contracts: 50 },
        { month: t('kazakInvest.months.aug'), tenders: 40, contracts: 150 },
        { month: t('kazakInvest.months.oct'), tenders: 80, contracts: 300 },
        { month: t('kazakInvest.months.oct'), tenders: 110, contracts: 400 },
        { month: t('kazakInvest.months.oct'), tenders: 125, contracts: 495 }
    ];

    const getRegionData = (t: any) => [
        { name: t('kazakInvest.regions.south'), value: 200 },
        { name: t('kazakInvest.regions.north'), value: 180 },
        { name: t('kazakInvest.regions.center'), value: 160 },
        { name: t('kazakInvest.regions.east'), value: 140 },
        { name: t('kazakInvest.regions.west'), value: 120 }
    ];
    const countryData = getCountryData(t);
    const suppliersData = getSuppliersData(t);
    const tenderStats = getTenderStats(t);
    const regionData = getRegionData(t);

    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);


    const handleRegionClick = (region: any) => {
        setSelectedRegion(region.name);
        console.log(t('kazakInvest.logs.regionSelected'), region);
    };

    const handleRegionHover = (region: any) => {
        setHoveredRegion(region ? region.name : null);
    };
    return (
        <Box p={4}>
            <Typography fontWeight={700}
                        variant={'h4'} gutterBottom>
                {t('kazakInvest.title')}
            </Typography>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('kazakInvest.sections.investmentActivity')}</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={tenderStats}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="tenders" stroke="#1976d2" name={t('kazakInvest.charts.tenderCount')} />
                                    <Line type="monotone" dataKey="contracts" stroke="#90caf9" name={t('kazakInvest.charts.contractVolume')} />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('kazakInvest.sections.topCountries')}</Typography>
                            {countryData.map((item) => (
                                <Box key={item.name} display="flex" alignItems="center" my={1}>
                                    <Typography width={120}>{item.name}</Typography>
                                    <LinearProgress variant="determinate" value={(item.value / 24.4) * 100} sx={{ flexGrow: 1, mx: 2 }} />
                                    <Typography>{item.value} M</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('kazakInvest.sections.keyIndicators')}</Typography>
                            <Typography>📌 {t('kazakInvest.indicators.tenders')}: <strong>125</strong></Typography>
                            <Typography>💰 {t('kazakInvest.indicators.contractVolume')}: <strong>$495M</strong></Typography>
                            <Typography>🏢 {t('kazakInvest.indicators.localSuppliers')}: <strong>60%</strong></Typography>
                            <Typography>✅ {t('kazakInvest.indicators.accreditedSuppliers')}: <strong>410</strong></Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('kazakInvest.sections.supplierAnalysis')}</Typography>
                            {suppliersData.map((item) => (
                                <Box key={item.name} display="flex" alignItems="center" my={1}>
                                    <Typography width={150}>{item.name}</Typography>
                                    <LinearProgress variant="determinate" value={(item.value / 32.9) * 100} sx={{ flexGrow: 1, mx: 2 }} />
                                    <Typography>{item.value} M</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('kazakInvest.sections.regionDemandMap')}</Typography>
                            <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <KazakhstanMap
                                    regions={kazakhstanRegions}
                                    onRegionClick={handleRegionClick}
                                    onRegionHover={handleRegionHover}
                                    defaultFill="#5764f7"
                                    hoverFill="#4254f5"
                                    activeFill="#3244d0"
                                />
                            </Box>
                            <Typography variant="body2" mt={2}>
                                * {t('kazakInvest.descriptions.mapDescription')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('kazakInvest.sections.regionDemandChart')}</Typography>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={regionData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#42a5f5" />
                                </BarChart>
                            </ResponsiveContainer>
                            <Typography variant="body2" mt={2}>
                                * {t('kazakInvest.descriptions.chartDescription')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default KazakInvestAnalytics;
